import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { CensusSnapshot } from 'src/shared/models/census-snapshot.model';
import { PageInterface } from '../../planner/page/page.interface';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { InpatientShiftRecordService } from 'src/shared/services/shift-record-inpatient.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowPageComponent } from 'src/app/workflow/base-classes/workflow-page/workflow-page.component';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { WorkflowPageUtil } from '../../base-classes/workflow-page/workflow-page.util';

@Component({
  selector: 'smt-review-previous',
  templateUrl: './review-previous.component.html',
  styleUrls: ['./review-previous.component.scss']
})
export class InpatientReviewPreviousComponent extends WorkflowPageComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public censusForm: UntypedFormGroup;
    public formGroups: UntypedFormGroup[];
    public loading: boolean;
    public prevShiftRecord: ShiftRecord;
    public updatedCensus: number;    
    public censusFormValues$;  
    public shiftRecord: ShiftRecord;

    constructor(
        private _inpatientShiftRecordService: InpatientShiftRecordService, 
        private _utility: WorkflowUtilityService, 
        private _router: Router) {
        super(_router);
    }

    ngOnInit() {
        super.initialize(this.page, () => this.initializeInpatient());        
    }

    initializeInpatient() { 
        this.censusForm = new UntypedFormGroup({
            admits: new UntypedFormControl(null, Validators.required),
            discharges: new UntypedFormControl(null, Validators.required)
        });

        this.prevShiftRecord = this.shiftRecord.prevShiftRecord;

        // In the edge case of the first shift block after going active,
        // We need to initialize the patient census record correctly.
        if (!this.prevShiftRecord) {
            let census = this.shiftRecord && this.shiftRecord.patientCensusRecord ? this.shiftRecord.patientCensusRecord.patients : null;
            this.censusForm.addControl('startingCensus', new UntypedFormControl(census, Validators.required));
        }

        if (!this.hasCompletePreviousShiftRecord()){
            this.censusForm.addControl('previousCensusControl', new UntypedFormControl(null, Validators.required));
        }

        if (this.shiftRecord.costCenter.inpatientConfiguration.transfers) {
            this.censusForm.addControl('transfersIn', new UntypedFormControl(null, Validators.required));
            this.censusForm.addControl('transfersOut', new UntypedFormControl(null, Validators.required));
        }

        // If we're coming back from another page, populate the appropriate fields
        if (this.prevShiftRecord && this.prevShiftRecord.actualCensusChurn) {
            this.censusForm.patchValue({
                admits: this.prevShiftRecord.actualCensusChurn.admits,
                transfersIn: this.prevShiftRecord.actualCensusChurn.transfersIn,
                transfersOut: this.prevShiftRecord.actualCensusChurn.transfersOut,
                discharges: this.prevShiftRecord.actualCensusChurn.discharges
            });
        }

        if (!this.hasCompletePreviousShiftRecord() && this.prevShiftRecord && this.prevShiftRecord.patientCensusRecord){
            this.censusForm.get('previousCensusControl').setValue(this.prevShiftRecord.patientCensusRecord.patients);
        }

        this.censusFormValues$ = this.censusForm.valueChanges.subscribe(values => {
            this.updateCensusValue(values);
        });

        this.updateCensusValue(this.censusForm.value);  
        
        this.formGroups = [ this.censusForm ];
    }

    isFormValid(): boolean {
        if (!this.prevShiftRecord) {
            return this.isStartingCensusValid();
        }
        
        return this.censusForm 
            && this.censusForm.valid 
            && this.isCensusFormValid()
            && this.isValid();
    }

    isValid(): boolean {
        return this.updatedCensus >= this.shiftRecord.costCenter.inpatientConfiguration.minCensus
            && this.updatedCensus <= this.shiftRecord.costCenter.inpatientConfiguration.maxCensus;
    }

    isCensusFormValid() : boolean {
        const hasTransfers = this.shiftRecord.costCenter.inpatientConfiguration.transfers;
        return WorkflowPageUtil.isValidCensusForm(this.censusForm, hasTransfers);
    }

    isStartingCensusValid() {
        if (this.hasCompletePreviousShiftRecord()) return true;

        let census = this.prevShiftRecord ? this.censusForm.value['previousCensusControl'] : this.censusForm.value['startingCensus'];

        return census >= this.shiftRecord.costCenter.inpatientConfiguration.minCensus
            && census <= this.shiftRecord.costCenter.inpatientConfiguration.maxCensus;
    }

    showEditedName() {
        return this.prevShiftRecord && this.prevShiftRecord.user;
    }

    setActualChurn() {
        if (!this.prevShiftRecord) {
            return;
        }
        this.prevShiftRecord.actualCensusChurn = new CensusSnapshot();

        this.prevShiftRecord.actualCensusChurn.costCenterId = this.prevShiftRecord.costCenterId;
        this.prevShiftRecord.actualCensusChurn.shiftBlockId = this.prevShiftRecord.shiftBlockId;
        this.prevShiftRecord.actualCensusChurn.admits = this.censusForm.value['admits'] || 0;
        this.prevShiftRecord.actualCensusChurn.transfersIn = this.censusForm.value['transfersIn'] || 0;
        this.prevShiftRecord.actualCensusChurn.transfersOut = this.censusForm.value['transfersOut'] || 0;
        this.prevShiftRecord.actualCensusChurn.discharges = this.censusForm.value['discharges'] || 0;
        this.prevShiftRecord.actualCensusChurn.isProjected = true;
    }

    setPatientCensus() {
        if (this.prevShiftRecord && !this.hasCompletePreviousShiftRecord()) {
            this.prevShiftRecord.patientCensusRecord.patients = +this.censusForm.value['previousCensusControl'];
        } 
    
        this.shiftRecord.patientCensusRecord.patients = this.updatedCensus;
    }

    setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.EnterCurrent;
        }
    }

    async save(): Promise<number> {
        // When saving be sure to clear the staffing plans.  This prevents any staffing plans that were created on later pages
        // from being saved on this page in case this page was nagivated to via the back button.
        this.shiftRecord.staffingPlans = [];

        this.setActualChurn();
        this.setPatientCensus();
        this.setStatus();
        if (!this.prevShiftRecord) {
            return this._inpatientShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
        }

        return await this._inpatientShiftRecordService.setShiftRecordWithAssociated(this.prevShiftRecord).then((prevShiftRecordId) => {
            this.shiftRecord.prevShiftRecordId = prevShiftRecordId;
            return this._inpatientShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
        }, 
        (errors) => { console.error(errors); return 0; });
    }

    shouldSkipSave(): boolean {
        return false;
    }

    hasCompletePreviousShiftRecord() {
        return this.shiftRecord.prevShiftRecord && this.shiftRecord.prevShiftRecord.status === ShiftRecordStatus.Done;
    }

    previousCensusValue() {
        if (!this.hasCompletePreviousShiftRecord()) {
            return '*';
        } 

        return this._utility.parseCensusValue(this.prevShiftRecord.patientCensusRecord.patients, this.prevShiftRecord.projectedCensusChurn);
    }

    projectedChurnValue(value) {
        if (!this.hasCompletePreviousShiftRecord()) {
            return '*';
        } 
        else {
            return this.prevShiftRecord.projectedCensusChurn[value];
        } 
    }

    formatShiftBlockTime() {
        return this._utility.formatShiftTime(this.prevShiftRecord.shiftBlock, this.prevShiftRecord.workflowTimeFacility);
    }

    getActualStaffCount(jobTitleId: number){
        let staffPlan = this.prevShiftRecord.staffingPlans.find(plan => plan.jobTitleId == jobTitleId);

        if (!staffPlan) return '*';

        return staffPlan.amount;
    }

    getRecommendedStaffCount(jobTitleId: number) {
        let staffPlan = this.prevShiftRecord.staffingPlans.find(plan => plan.jobTitleId == jobTitleId);

        if (!staffPlan) return '*';

        return staffPlan.recommended;
    }

    updateCensusValue(values) {
        let census = 0;

        for(let i in values){
            if (values[i] && !Number.isInteger(values[i])){
                // we only allow integers as census values
                this.censusForm.get(i).setValue(Math.round(values[i]));
            }
        }

        if (!this.prevShiftRecord) {
            census = values['startingCensus'] || 0;
        }
        else if (!this.hasCompletePreviousShiftRecord()){
            census = values['previousCensusControl'] || 0;
        } 
        else {
            census = this.prevShiftRecord.patientCensusRecord.patients;
        }

        this.updatedCensus = this._utility.parseCensusValue(census, values);
    }
}
